import type { Locale } from '../index'

/**
 * Help-centre page copy in all three locales (en / zh-Hant / zh-Hans).
 *
 * zh-Hant (Traditional — Taiwan / HK) and zh-Hans (Simplified — Mainland) are
 * distinct, hand-written translations, not mechanical conversions. Indexed FAQ
 * keys (help.faq.N.*) mirror the order of CATEGORIES in Help.tsx. Answers that
 * contain inline links are split into segments (a1 / link / a2); the plain *.a
 * key holds the full answer text used for client-side search matching.
 */
export const help: Record<Locale, Record<string, string>> = {
  en: {
    'help.head.title': 'Help centre — Geo4Logistics',
    'help.head.description':
      'Answers about tracking, quotes, booking, documents, customs, the customer portal and billing — plus ways to reach a person.',

    'help.hero.title': 'How can we help?',
    'help.hero.body':
      "Search the help centre, browse a topic, or jump straight to chat — whatever's fastest.",
    'help.search.placeholder': "Search e.g. 'how do I track', 'invoice', 'customs'…",
    'help.search.label': 'Search the help centre',

    'help.card.tracking.title': 'Tracking',
    'help.card.tracking.desc': 'Statuses, ETAs & exceptions',
    'help.card.quotes.title': 'Quotes & booking',
    'help.card.quotes.desc': 'Pricing, transit time, confirming',
    'help.card.docs.title': 'Documents & customs',
    'help.card.docs.desc': 'B/L, clearance, paperwork',
    'help.card.portal.title': 'Account & portal',
    'help.card.portal.desc': 'Logins, team, multi-company',
    'help.card.billing.title': 'Invoices & billing',
    'help.card.billing.desc': 'Invoices, payment, charges',
    'help.card.contact.title': 'Contact support',
    'help.card.contact.desc': 'Talk to a real person',

    'help.cat.tracking.title': 'Tracking',
    'help.cat.quotes.title': 'Quotes & booking',
    'help.cat.docs.title': 'Documents & customs',
    'help.cat.portal.title': 'Account & portal',
    'help.cat.billing.title': 'Invoices & billing',

    'help.faq.label': 'Frequently asked questions',

    'help.faq.0.q': 'How do I track my shipment?',
    'help.faq.0.a':
      'Enter your B/L, container or Geo4 reference on the Track page for live, leg-by-leg status. For documents and full history, log in to the customer portal.',
    'help.faq.0.a1': 'Enter your B/L, container or Geo4 reference on the ',
    'help.faq.0.link': 'Track',
    'help.faq.0.a2':
      ' page for live, leg-by-leg status. For documents and full history, log in to the customer portal.',

    'help.faq.1.q': 'What do the statuses mean?',
    'help.faq.1.a':
      'Booked — space reserved, not yet departed. In Transit — moving between legs. Customs Hold — at clearance. Delayed — a milestone slipped (we update the revised ETA). Delivered — completed with proof of delivery.',
    'help.faq.1.s1': 'Booked',
    'help.faq.1.s1d': '— space reserved, not yet departed.',
    'help.faq.1.s2': 'In Transit',
    'help.faq.1.s2d': '— moving between legs.',
    'help.faq.1.s3': 'Customs Hold',
    'help.faq.1.s3d': '— at clearance.',
    'help.faq.1.s4': 'Delayed',
    'help.faq.1.s4d': '— a milestone slipped (we update the revised ETA).',
    'help.faq.1.s5': 'Delivered',
    'help.faq.1.s5d': '— completed with proof of delivery.',

    'help.faq.2.q': 'How often is tracking updated?',
    'help.faq.2.a':
      'Milestones update as carriers report them and as our operators confirm each leg — typically several times a day on an active shipment. Exceptions trigger an alert in the portal.',

    'help.faq.3.q': 'My vessel is delayed — what happens?',
    'help.faq.3.a':
      'We set the status to Delayed and post a revised ETA as soon as we have it, with a note explaining why.',

    'help.faq.4.q': 'How do I get a quote?',
    'help.faq.4.a':
      'Use the quote form with your origin, destination, mode and cargo details. We reply with itemised pricing and transit time — usually within 1–2 business days.',
    'help.faq.4.a1': 'Use the ',
    'help.faq.4.link': 'quote form',
    'help.faq.4.a2':
      ' with your origin, destination, mode and cargo details. We reply with itemised pricing and transit time — usually within 1–2 business days.',

    'help.faq.5.q': 'How long is a quote valid?',
    'help.faq.5.a':
      'Each quote shows an expiry date in the portal. Ocean and air rates move with the market, so confirm before it expires to lock the price.',

    'help.faq.6.q': 'Can you recommend the best mode?',
    'help.faq.6.a':
      'Yes — tell us your deadline and budget and a coordinator will weigh ocean, rail, air and road for your lane. The Services page has a quick comparison to start.',
    'help.faq.6.a1':
      'Yes — tell us your deadline and budget and a coordinator will weigh ocean, rail, air and road for your lane. The ',
    'help.faq.6.link': 'Services',
    'help.faq.6.a2': ' page has a quick comparison to start.',

    'help.faq.7.q': 'Which documents do you need from me?',
    'help.faq.7.a':
      "Typically a commercial invoice and packing list, plus any licences for controlled goods. We tell you exactly what's needed per shipment, and you upload it once in the portal.",

    'help.faq.8.q': 'Do you handle customs clearance?',
    'help.faq.8.a':
      "Yes. Our brokerage specialists handle HS classification, duties and clearance at origin and destination so your cargo doesn't sit.",

    'help.faq.9.q': 'Where do I find my B/L and arrival notice?',
    'help.faq.9.a':
      "All shipment documents — HBL, packing list, arrival notice, customs docs — are on the shipment's Documents tab in the portal, ready to download.",

    'help.faq.10.q': 'How do I get access to the portal?',
    'help.faq.10.a':
      "Once you're set up as a customer we send an invitation to your email. Open the portal to explore a demo of what your team will see.",
    'help.faq.10.a1': "Once you're set up as a customer we send an invitation to your email.",
    'help.faq.10.link': 'Open the portal',
    'help.faq.10.a2': ' to explore a demo of what your team will see.',

    'help.faq.11.q': 'Can I invite my colleagues?',
    'help.faq.11.a':
      'Yes. A company admin can invite team members and set their access from Settings → Team. Larger groups can manage multiple companies under one login.',

    'help.faq.12.q': 'Is there an API?',
    'help.faq.12.a':
      'Yes — a developer API and webhooks let you push bookings and pull status into your own systems. API access is provided on request: contact us and we’ll enable it for your account and share the developer docs.',
    'help.faq.12.a1':
      'Yes — a developer API and webhooks let you push bookings and pull status into your own systems. API access is provided on request: contact us and we’ll enable it for your account. See the ',
    'help.faq.12.link': 'developer docs',
    'help.faq.12.a2': '.',

    'help.faq.13.q': 'Where are my invoices?',
    'help.faq.13.a':
      'On the Invoices page in the portal, with status (Issued / Paid), due date and a downloadable PDF for each.',

    'help.faq.14.q': 'Are quotes all-in, or are there extra charges?',
    'help.faq.14.a':
      "Quotes are itemised so you can see every line. If something unforeseen comes up (e.g. a customs exam), we flag it and confirm with you before it's billed.",

    'help.faq.15.q': 'I have a question about a charge.',
    'help.faq.15.a':
      "Message us from the shipment or invoice in the portal, or contact support — we'll walk you through it line by line.",
    'help.faq.15.a1': 'Message us from the shipment or invoice in the portal, or',
    'help.faq.15.link': 'contact support',
    'help.faq.15.a2': " — we'll walk you through it line by line.",

    'help.results.one': '1 article matches your search.',
    'help.results.other': '{count} articles match your search.',

    'help.empty.text': 'No articles match your search. Try a different term, or',
    'help.empty.link': 'contact us',
    'help.empty.after': '.',

    'help.cta.title': 'Still need a hand?',
    'help.cta.body':
      'Our support team replies in under two hours on average, in English or Chinese. Start a chat or send us a message.',
    'help.cta.chat': 'Start a chat',
    'help.cta.contact': 'Contact us',
  },

  'zh-Hant': {
    'help.head.title': '支援中心 — Geo4Logistics',
    'help.head.description':
      '關於貨態查詢、報價、訂艙、文件、報關、客戶平台與帳務的常見問題解答，以及聯絡專人的方式。',

    'help.hero.title': '有什麼可以協助您？',
    'help.hero.body': '搜尋支援中心、瀏覽主題，或直接開啟對話 — 怎麼快就怎麼來。',
    'help.search.placeholder': '搜尋，例如「如何查詢貨態」、「發票」、「報關」…',
    'help.search.label': '搜尋支援中心',

    'help.card.tracking.title': '貨態查詢',
    'help.card.tracking.desc': '狀態、預計抵達時間與異常',
    'help.card.quotes.title': '報價與訂艙',
    'help.card.quotes.desc': '價格、運送時間、確認',
    'help.card.docs.title': '文件與報關',
    'help.card.docs.desc': '提單、清關、文件作業',
    'help.card.portal.title': '帳戶與平台',
    'help.card.portal.desc': '登入、團隊、多公司管理',
    'help.card.billing.title': '發票與帳務',
    'help.card.billing.desc': '發票、付款、費用',
    'help.card.contact.title': '聯絡客服',
    'help.card.contact.desc': '與真人專員對話',

    'help.cat.tracking.title': '貨態查詢',
    'help.cat.quotes.title': '報價與訂艙',
    'help.cat.docs.title': '文件與報關',
    'help.cat.portal.title': '帳戶與平台',
    'help.cat.billing.title': '發票與帳務',

    'help.faq.label': '常見問題',

    'help.faq.0.q': '我要如何查詢貨件貨態？',
    'help.faq.0.a':
      '於「貨態查詢」頁面輸入您的提單號、貨櫃號或 Geo4 參考編號，即可取得即時、逐段的運送狀態。如需文件與完整歷程，請登入客戶平台。',
    'help.faq.0.a1': '於',
    'help.faq.0.link': '貨態查詢',
    'help.faq.0.a2':
      '頁面輸入您的提單號、貨櫃號或 Geo4 參考編號，即可取得即時、逐段的運送狀態。如需文件與完整歷程，請登入客戶平台。',

    'help.faq.1.q': '各項狀態代表什麼意思？',
    'help.faq.1.a':
      '已訂艙 — 已預留艙位，尚未啟運。運送中 — 正在各段運輸之間移動。報關待放 — 正在辦理清關。延誤 — 某個里程碑延後（我們會更新修正後的預計抵達時間）。已送達 — 已完成並附上簽收證明。',
    'help.faq.1.s1': '已訂艙',
    'help.faq.1.s1d': '— 已預留艙位，尚未啟運。',
    'help.faq.1.s2': '運送中',
    'help.faq.1.s2d': '— 正在各段運輸之間移動。',
    'help.faq.1.s3': '報關待放',
    'help.faq.1.s3d': '— 正在辦理清關。',
    'help.faq.1.s4': '延誤',
    'help.faq.1.s4d': '— 某個里程碑延後（我們會更新修正後的預計抵達時間）。',
    'help.faq.1.s5': '已送達',
    'help.faq.1.s5d': '— 已完成並附上簽收證明。',

    'help.faq.2.q': '貨態多久更新一次？',
    'help.faq.2.a':
      '里程碑會在承運人回報以及我們的操作人員確認各段運輸時更新 — 運送中的貨件通常每天更新數次。發生異常時，平台會觸發提醒。',

    'help.faq.3.q': '我的船班延誤了，會怎麼處理？',
    'help.faq.3.a':
      '我們會將狀態設為「延誤」，並在取得資訊後立即公布修正後的預計抵達時間，並附上原因說明。',

    'help.faq.4.q': '我要如何取得報價？',
    'help.faq.4.a':
      '請使用報價表單填寫起運地、目的地、運輸方式與貨物細節。我們會回覆逐項列明的價格與運送時間 — 通常於一至兩個工作天內。',
    'help.faq.4.a1': '請使用',
    'help.faq.4.link': '報價表單',
    'help.faq.4.a2':
      '填寫起運地、目的地、運輸方式與貨物細節。我們會回覆逐項列明的價格與運送時間 — 通常於一至兩個工作天內。',

    'help.faq.5.q': '報價的有效期限多長？',
    'help.faq.5.a':
      '每筆報價都會在平台上顯示到期日。海運與空運費率會隨市場波動，請於到期前確認以鎖定價格。',

    'help.faq.6.q': '你們能推薦最合適的運輸方式嗎？',
    'help.faq.6.a':
      '當然 — 告訴我們您的期限與預算，協調專員會就您的航線權衡海運、鐵路運輸、空運與陸運。「服務」頁面也提供快速比較供您參考。',
    'help.faq.6.a1':
      '當然 — 告訴我們您的期限與預算，協調專員會就您的航線權衡海運、鐵路運輸、空運與陸運。',
    'help.faq.6.link': '服務',
    'help.faq.6.a2': '頁面也提供快速比較供您參考。',

    'help.faq.7.q': '你們需要我提供哪些文件？',
    'help.faq.7.a':
      '通常需要商業發票與裝箱單，以及管制貨物所需的相關許可證。我們會針對每筆貨件明確告知所需文件，您只需在平台上一次上傳即可。',

    'help.faq.8.q': '你們有提供報關服務嗎？',
    'help.faq.8.a':
      '有的。我們的報關專員會處理 HS 稅則分類、稅費以及起運地與目的地的清關，讓您的貨物不會滯留。',

    'help.faq.9.q': '我要在哪裡找到我的提單與到貨通知？',
    'help.faq.9.a':
      '所有貨件文件 — 分提單、裝箱單、到貨通知、報關文件 — 都在平台中該貨件的「文件」分頁，隨時可供下載。',

    'help.faq.10.q': '我要如何取得平台的使用權限？',
    'help.faq.10.a':
      '一旦您成為我們的客戶，我們便會寄送邀請至您的電子郵件。開啟客戶平台即可體驗您的團隊將看到的示範內容。',
    'help.faq.10.a1': '一旦您成為我們的客戶，我們便會寄送邀請至您的電子郵件。',
    'help.faq.10.link': '開啟客戶平台',
    'help.faq.10.a2': '即可體驗您的團隊將看到的示範內容。',

    'help.faq.11.q': '我可以邀請同事一起使用嗎？',
    'help.faq.11.a':
      '可以。公司管理員可從「設定 → 團隊」邀請團隊成員並設定其權限。規模較大的集團可在同一登入帳號下管理多家公司。',

    'help.faq.12.q': '有提供 API 嗎？',
    'help.faq.12.a':
      '有的 — 透過我們的開發者 API 與 webhook，可將訂艙推送至您自己的系統並拉取狀態。API 存取權需提出申請：與我們聯繫，我們會為您的帳戶開通並提供開發者文件。',
    'help.faq.12.a1':
      '有的 — 透過我們的開發者 API 與 webhook，可將訂艙推送至您自己的系統並拉取狀態。API 存取權需提出申請：與我們聯繫，我們會為您的帳戶開通。請參閱',
    'help.faq.12.link': '開發者文件',
    'help.faq.12.a2': '。',

    'help.faq.13.q': '我的發票在哪裡？',
    'help.faq.13.a':
      '在平台的「發票」頁面，每筆都顯示狀態（已開立／已付款）、到期日，並提供可下載的 PDF。',

    'help.faq.14.q': '報價是全包價，還是會有額外費用？',
    'help.faq.14.a':
      '報價會逐項列明，讓您看清每一項費用。若發生無法預期的狀況（例如海關查驗），我們會主動告知並在計費前與您確認。',

    'help.faq.15.q': '我想詢問某項費用。',
    'help.faq.15.a': '您可在平台中從該貨件或發票傳訊息給我們，或聯絡客服 — 我們會逐項為您說明。',
    'help.faq.15.a1': '您可在平台中從該貨件或發票傳訊息給我們，或',
    'help.faq.15.link': '聯絡客服',
    'help.faq.15.a2': '— 我們會逐項為您說明。',

    'help.results.one': '有 1 篇文章符合您的搜尋。',
    'help.results.other': '有 {count} 篇文章符合您的搜尋。',

    'help.empty.text': '沒有符合您搜尋的文章。請嘗試其他關鍵字，或',
    'help.empty.link': '聯絡我們',
    'help.empty.after': '。',

    'help.cta.title': '還需要協助嗎？',
    'help.cta.body':
      '我們的支援團隊平均於兩小時內回覆，可使用英文或中文。歡迎開始對話或傳訊息給我們。',
    'help.cta.chat': '開始對話',
    'help.cta.contact': '聯絡我們',
  },

  'zh-Hans': {
    'help.head.title': '帮助中心 — Geo4Logistics',
    'help.head.description':
      '关于货物追踪、报价、订舱、文件、报关、客户平台与账务的常见问题解答，以及联系人工的方式。',

    'help.hero.title': '有什么可以帮您？',
    'help.hero.body': '搜索帮助中心、浏览主题，或直接开始对话 — 怎么快就怎么来。',
    'help.search.placeholder': '搜索，例如“如何追踪货物”、“发票”、“报关”…',
    'help.search.label': '搜索帮助中心',

    'help.card.tracking.title': '货物追踪',
    'help.card.tracking.desc': '状态、预计到达时间与异常',
    'help.card.quotes.title': '报价与订舱',
    'help.card.quotes.desc': '价格、运输时间、确认',
    'help.card.docs.title': '文件与报关',
    'help.card.docs.desc': '提单、清关、单证',
    'help.card.portal.title': '账户与平台',
    'help.card.portal.desc': '登录、团队、多公司管理',
    'help.card.billing.title': '发票与账务',
    'help.card.billing.desc': '发票、付款、费用',
    'help.card.contact.title': '联系客服',
    'help.card.contact.desc': '与真人专员对话',

    'help.cat.tracking.title': '货物追踪',
    'help.cat.quotes.title': '报价与订舱',
    'help.cat.docs.title': '文件与报关',
    'help.cat.portal.title': '账户与平台',
    'help.cat.billing.title': '发票与账务',

    'help.faq.label': '常见问题',

    'help.faq.0.q': '我该如何追踪我的货件？',
    'help.faq.0.a':
      '在“货物追踪”页面输入您的提单号、集装箱号或 Geo4 参考编号，即可获取实时、逐段的运输状态。如需文件与完整历史记录，请登录客户平台。',
    'help.faq.0.a1': '在',
    'help.faq.0.link': '货物追踪',
    'help.faq.0.a2':
      '页面输入您的提单号、集装箱号或 Geo4 参考编号，即可获取实时、逐段的运输状态。如需文件与完整历史记录，请登录客户平台。',

    'help.faq.1.q': '各项状态分别代表什么？',
    'help.faq.1.a':
      '已订舱 — 已预留舱位，尚未启运。运输中 — 正在各段运输之间移动。报关待放 — 正在办理清关。延误 — 某个里程碑延后（我们会更新修正后的预计到达时间）。已送达 — 已完成并附有签收凭证。',
    'help.faq.1.s1': '已订舱',
    'help.faq.1.s1d': '— 已预留舱位，尚未启运。',
    'help.faq.1.s2': '运输中',
    'help.faq.1.s2d': '— 正在各段运输之间移动。',
    'help.faq.1.s3': '报关待放',
    'help.faq.1.s3d': '— 正在办理清关。',
    'help.faq.1.s4': '延误',
    'help.faq.1.s4d': '— 某个里程碑延后（我们会更新修正后的预计到达时间）。',
    'help.faq.1.s5': '已送达',
    'help.faq.1.s5d': '— 已完成并附有签收凭证。',

    'help.faq.2.q': '货物追踪多久更新一次？',
    'help.faq.2.a':
      '里程碑会在承运人上报以及我们的操作人员确认各段运输时更新 — 运输中的货件通常每天更新数次。出现异常时，平台会触发提醒。',

    'help.faq.3.q': '我的船班延误了，会如何处理？',
    'help.faq.3.a':
      '我们会将状态设为“延误”，并在获取信息后立即发布修正后的预计到达时间，并附上原因说明。',

    'help.faq.4.q': '我该如何获取报价？',
    'help.faq.4.a':
      '请使用报价表单填写起运地、目的地、运输方式与货物详情。我们会回复逐项列明的价格与运输时间 — 通常在一到两个工作日内。',
    'help.faq.4.a1': '请使用',
    'help.faq.4.link': '报价表单',
    'help.faq.4.a2':
      '填写起运地、目的地、运输方式与货物详情。我们会回复逐项列明的价格与运输时间 — 通常在一到两个工作日内。',

    'help.faq.5.q': '报价的有效期有多长？',
    'help.faq.5.a':
      '每份报价都会在平台上显示到期日。海运与空运费率会随市场波动，请在到期前确认以锁定价格。',

    'help.faq.6.q': '你们能推荐最合适的运输方式吗？',
    'help.faq.6.a':
      '当然 — 告诉我们您的期限与预算，协调专员会就您的航线权衡海运、铁路运输、空运与公路运输。“服务”页面也提供快速对比供您参考。',
    'help.faq.6.a1':
      '当然 — 告诉我们您的期限与预算，协调专员会就您的航线权衡海运、铁路运输、空运与公路运输。',
    'help.faq.6.link': '服务',
    'help.faq.6.a2': '页面也提供快速对比供您参考。',

    'help.faq.7.q': '你们需要我提供哪些文件？',
    'help.faq.7.a':
      '通常需要商业发票与装箱单，以及管制货物所需的相关许可证。我们会针对每件货件明确告知所需文件，您只需在平台上一次性上传即可。',

    'help.faq.8.q': '你们提供报关服务吗？',
    'help.faq.8.a':
      '提供。我们的报关专员会处理 HS 编码归类、税费以及起运地与目的地的清关，让您的货物不会滞留。',

    'help.faq.9.q': '我在哪里可以找到我的提单与到货通知？',
    'help.faq.9.a':
      '所有货件文件 — 分提单、装箱单、到货通知、报关文件 — 都在平台中该货件的“文件”标签页，随时可供下载。',

    'help.faq.10.q': '我该如何获取平台的使用权限？',
    'help.faq.10.a':
      '一旦您成为我们的客户，我们便会向您的电子邮箱发送邀请。打开客户平台即可体验您的团队将看到的演示内容。',
    'help.faq.10.a1': '一旦您成为我们的客户，我们便会向您的电子邮箱发送邀请。',
    'help.faq.10.link': '打开客户平台',
    'help.faq.10.a2': '即可体验您的团队将看到的演示内容。',

    'help.faq.11.q': '我可以邀请同事一起使用吗？',
    'help.faq.11.a':
      '可以。公司管理员可从“设置 → 团队”邀请团队成员并设置其权限。规模较大的集团可在同一登录账号下管理多家公司。',

    'help.faq.12.q': '有提供 API 吗？',
    'help.faq.12.a':
      '有的 — 通过我们的开发者 API 与 webhook，可将订舱推送至您自己的系统并拉取状态。API 访问权限需提出申请：与我们联系，我们会为您的账户开通并提供开发者文档。',
    'help.faq.12.a1':
      '有的 — 通过我们的开发者 API 与 webhook，可将订舱推送至您自己的系统并拉取状态。API 访问权限需提出申请：与我们联系，我们会为您的账户开通。请参阅',
    'help.faq.12.link': '开发者文档',
    'help.faq.12.a2': '。',

    'help.faq.13.q': '我的发票在哪里？',
    'help.faq.13.a':
      '在平台的“发票”页面，每笔都显示状态（已开具／已付款）、到期日，并提供可下载的 PDF。',

    'help.faq.14.q': '报价是全包价，还是会有额外费用？',
    'help.faq.14.a':
      '报价会逐项列明，让您看清每一项费用。若出现无法预料的情况（例如海关查验），我们会主动告知并在计费前与您确认。',

    'help.faq.15.q': '我想咨询某项费用。',
    'help.faq.15.a': '您可在平台中从该货件或发票给我们发消息，或联系客服 — 我们会逐项为您说明。',
    'help.faq.15.a1': '您可在平台中从该货件或发票给我们发消息，或',
    'help.faq.15.link': '联系客服',
    'help.faq.15.a2': '— 我们会逐项为您说明。',

    'help.results.one': '有 1 篇文章符合您的搜索。',
    'help.results.other': '有 {count} 篇文章符合您的搜索。',

    'help.empty.text': '没有符合您搜索的文章。请尝试其他关键词，或',
    'help.empty.link': '联系我们',
    'help.empty.after': '。',

    'help.cta.title': '还需要帮助吗？',
    'help.cta.body':
      '我们的支持团队平均在两小时内回复，可使用英文或中文。欢迎开始对话或给我们发消息。',
    'help.cta.chat': '开始对话',
    'help.cta.contact': '联系我们',
  },
}
